import { Flag02Icon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Link } from "react-router-dom";
import { Show } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { VerificationNames } from "shared/types";
import { cx } from "shared/utils";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";

const vendorLabel = (vendor?: string | null) =>
  vendor === "persona" ? "Persona" : "Plaid";

const reportUrl = ({ metadata, url }: Notification) =>
  metadata.application_id && metadata.applicant_id
    ? `/applications/${metadata.application_id}?applicantId=${metadata.applicant_id}&report=${VerificationNames.IdentityVerification}`
    : url;

export const IdvBypassNotificationCard = ({ item }: { item: Notification }) => {
  const queryClient = useQueryClient();
  const { id, read_at, metadata, created_at } = item;

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    }
  });

  const handleClick = () => {
    if (!read_at) markAsRead([id]);
  };

  const applicantName = metadata.applicant_name || "An applicant";
  const resolved = Boolean(metadata.resolved);
  const place = [metadata.property_name, metadata.unit_name]
    .filter(Boolean)
    .join(" · ");

  return (
    <Link
      to={reportUrl(item)}
      onClick={handleClick}
      className="p-6 bg-white hover:bg-alpha-40 cursor-pointer flex flex-col gap-3 w-full"
    >
      <div className="flex items-center justify-between gap-4">
        <span
          className={cx(
            "flex items-center gap-1.5 text-sm font-medium",
            resolved ? "text-content-300" : "text-danger-500"
          )}
        >
          <HugeiconsIcon icon={Flag02Icon} size={16} aria-hidden />
          {resolved ? "IDV bypass resolved" : "Asked to bypass IDV"}
        </span>
        <p className="text-content-300 font-medium text-sm shrink-0">
          {formatCalendar(created_at)}
        </p>
      </div>
      <div className="flex items-center gap-4 min-w-0">
        <span
          className={cx(
            "size-2.5 rounded-full shrink-0",
            read_at ? "bg-alpha-40" : "bg-primary-500"
          )}
        />
        <Avatar name={applicantName} size={44} className="font-bold" />
        <div className="min-w-0">
          <p className="text-lg font-bold text-content-1000 truncate">
            {applicantName}
          </p>
          <p className="text-base text-content-400 truncate">
            {applicantName} couldn&apos;t finish identity verification (
            {vendorLabel(metadata.idv_vendor)}) and asked you to bypass it
          </p>
          <Show if={Boolean(place)}>
            <p className="text-sm text-content-300 truncate">{place}</p>
          </Show>
        </div>
      </div>
    </Link>
  );
};
